/**
 * =============================================================================
 * complementary-products.js
 * =============================================================================
 * Powers the "Frequently Bought Together" section on the product page.
 *
 * Lets the customer tick / select complementary products and add them to the
 * cart alongside the main product (or on their own, for enquire-only products)
 * in a single action. Reuses the existing MP.Extensions.Cart.add() flow so the
 * standard minicart / cart-count / success behaviour is preserved - no changes
 * to the core product form or cart functionality.
 *
 * Markup is rendered by snippets/product-complementary-products.liquid.
 * =============================================================================
 */

class ComplementaryProducts extends HTMLElement {
  // --- Configuration (host attributes, set in product-complementary-products.liquid) ---

  /**
   * Whether the main (parent) product is part of this bundle.
   *
   * true  — normal purchasable main product: the combined button adds the main
   *         product plus any ticked complementary items; the running total
   *         includes the main variant price × quantity.
   * false — enquire-only main product (hide_product_pricing): the main product
   *         cannot be added to cart, so the button only adds selected
   *         complementary items and stays disabled until at least one is ticked.
   *
   * Reflected from the `include-main` boolean host attribute.
   *
   * @type {boolean}
   */
  includeMain;

  /** Parent product ID, sent with GA4 complementary_product_added_to_cart events. @type {string} */
  parentProductId;

  /** ID of the main product form on the page; used to read variant, qty and properties on add. @type {HTMLFormElement|null} */
  mainForm;

  // --- Cached DOM references ---

  /** Complementary product card elements within this section. @type {HTMLElement[]} */
  cards;
  /** Element displaying the combined price total. @type {HTMLElement|null} */
  totalElement;
  /** "+ N item(s)" badge shown when complementary products are selected. @type {HTMLElement|null} */
  countElement;
  /** Combined add-to-cart button for the bundle. @type {HTMLButtonElement|null} */
  addButton;
  /** Bootstrap modal shown when a multi-variant card is ticked without a variant chosen. @type {HTMLElement|null} */
  variantRequiredModal;

  // --- Main product state (kept in sync when includeMain is true) ---

  /** Current main variant price in cents. @type {number} */
  mainPrice;
  /** Current main product quantity from the product form. @type {number} */
  mainQuantity;

  constructor() {
    super();

    // includeMain mirrors Liquid's include_main (true unless main product is enquire-only)
    this.includeMain = this.hasAttribute("include-main");
    this.parentProductId = this.getAttribute("parent-product-id");
    this.mainPrice = parseInt(this.getAttribute("main-price"), 10) || 0;
    this.mainQuantity = 1;

    // Cached DOM references
    this.mainForm = document.getElementById(this.getAttribute("main-form-id"));
    this.cards = Array.from(this.querySelectorAll("[data-complementary-card]"));
    this.totalElement = this.querySelector("[data-complementary-total]");
    this.countElement = this.querySelector("[data-complementary-count]");
    this.addButton = this.querySelector("[data-complementary-add]");
    this.addButtonLabel = this.querySelector("[data-complementary-add-label]");
    this.variantRequiredModal = this.querySelector("[data-variant-required-modal]");

    // Selection events from the cards
    this.addEventListener("change", this.onCardChange.bind(this));

    // The combined add to cart action
    this.addButton?.addEventListener("click", this.onAdd.bind(this));

    // Only track main variant/qty when the main product is part of the bundle;
    // enquire-only mains have no price to show and are never added to cart.
    const productContainer = this.closest(".product-container");
    if (productContainer && this.includeMain) {
      productContainer.addEventListener("variant:changed", this.onMainVariantChanged.bind(this));
      productContainer.addEventListener("quantity:changed", this.onMainQuantityChanged.bind(this));

      const quantityInput = this.mainForm?.querySelector("[name='quantity']");
      if (quantityInput) {
        this.mainQuantity = parseInt(quantityInput.value, 10) || 1;
      }
    }

    // Total, count badge and button state are already correct from Liquid —
    // no complementary items are selected on first paint, so skip recalculate()
    // until the customer interacts (first call is on card change).
  }

  /**
   * Handle any change within the section (checkbox tick or variant select)
   *
   * @param {Event} e
   */
  onCardChange(e) {
    const variantSelect = e.target.closest("[data-complementary-variant]");
    if (variantSelect) {
      this.onVariantSelectChange(variantSelect);
    }

    const checkbox = e.target.closest("[data-complementary-checkbox]");
    if (checkbox) {
      this.onCheckboxChange(checkbox);
    }

    this.recalculate();
  }

  /**
   * Validate a manual checkbox tick. Multi-variant products auto-tick their
   * checkbox when a variant is chosen; if the customer ticks the box themselves
   * without a variant selected, the product has nothing to add - so we revert
   * the tick and warn them to choose a variant first.
   *
   * @param {HTMLInputElement} checkbox
   */
  onCheckboxChange(checkbox) {
    if (!checkbox.checked) return;

    const card = checkbox.closest("[data-complementary-card]");
    const select = card.querySelector("[data-complementary-variant]");

    if (select && select.value === "") {
      checkbox.checked = false;
      this.showVariantRequiredModal();
    }
  }

  /**
   * Show the "please choose a variant" warning modal
   */
  showVariantRequiredModal() {
    if (this.variantRequiredModal && window.bootstrap) {
      bootstrap.Modal.getOrCreateInstance(this.variantRequiredModal).show();
    }
  }

  /**
   * When a multi-variant complementary product has a variant chosen, auto-tick
   * its checkbox and refresh its displayed price (FR4).
   *
   * @param {HTMLSelectElement} select
   */
  onVariantSelectChange(select) {
    const card = select.closest("[data-complementary-card]");
    const checkbox = card.querySelector("[data-complementary-checkbox]");
    const option = select.options[select.selectedIndex];
    const hasVariant = select.value !== "";

    if (checkbox) checkbox.checked = hasVariant;

    const priceElement = card.querySelector("[data-complementary-price]");
    if (priceElement && hasVariant && option?.dataset.price) {
      priceElement.textContent = MP.Utilities.formatMoney(parseInt(option.dataset.price, 10), window.theme?.moneyFormat);
    }
  }

  /**
   * Update the stored main price when the main variant changes
   *
   * @param {CustomEvent} e
   */
  onMainVariantChanged(e) {
    if (e.detail?.variant) {
      this.mainPrice = parseInt(e.detail.variant.price, 10) || 0;
      this.recalculate();
    }
  }

  /**
   * Update the stored main quantity when it changes
   *
   * @param {CustomEvent} e
   */
  onMainQuantityChanged(e) {
    this.mainQuantity = parseInt(e.detail?.quantity, 10) || 1;
    this.recalculate();
  }

  /**
   * Get all currently selected complementary line items
   *
   * @returns {Array<{card: HTMLElement, id: string, price: number}>}
   */
  getSelectedItems() {
    const selected = [];
    this.cards.forEach(card => {
      const checkbox = card.querySelector("[data-complementary-checkbox]");
      if (!checkbox || !checkbox.checked) return;

      const select = card.querySelector("[data-complementary-variant]");
      let id;
      let price;
      // Multi-variant cards: id and price come from the selected option.
      // Single-variant cards: both are stored on the checkbox itself.
      if (select) {
        id = select.value;
        price = parseInt(select.options[select.selectedIndex]?.dataset.price, 10);
      } else {
        id = checkbox.value;
        price = parseInt(checkbox.dataset.price, 10);
      }

      if (id) selected.push({ card, id, price: price || 0 });
    });
    return selected;
  }

  /**
   * Recalculate the running total, item count badge and add button state
   */
  recalculate() {
    const selected = this.getSelectedItems();

    // Enquire-only mains contribute nothing to the total; only complementary prices count.
    let total = this.includeMain ? this.mainPrice * this.mainQuantity : 0;
    selected.forEach(item => { total += item.price; });

    if (this.totalElement) {
      this.totalElement.textContent = MP.Utilities.formatMoney(total, window.theme?.moneyFormat);
    }

    this.updateCount(selected.length);

    // For enquire-only main products, the button only adds complementary items,
    // so it stays disabled and muted (btn-light-grey) until at least one item is
    // selected, then becomes active (btn-primary).
    if (this.addButton && !this.includeMain) {
      const hasSelection = selected.length > 0;
      this.addButton.disabled = !hasSelection;
      this.addButton.classList.toggle("btn-primary", hasSelection);
      this.addButton.classList.toggle("btn-light-grey", !hasSelection);

      if (this.addButtonLabel) {
        this.addButtonLabel.textContent = hasSelection
          ? this.addButton.dataset.labelAdd
          : this.addButton.dataset.labelSelect;
      }
    }
  }

  /**
   * Update the "+ N item(s)" badge
   *
   * @param {number} count
   */
  updateCount(count) {
    if (!this.countElement) return;
    if (count > 0) {
      const label = count === 1
        ? this.countElement.dataset.labelSingular
        : this.countElement.dataset.labelPlural;
      this.countElement.textContent = `+ ${count} ${label}`;
      this.countElement.classList.remove("d-none");
    } else {
      this.countElement.classList.add("d-none");
    }
  }

  /**
   * Build the line items array and add everything to the cart in one request
   */
  onAdd() {
    if (this.addButton.classList.contains("loading")) return;

    const items = [];

    // Skip the main line item when enquire-only — only complementary selections are added.
    if (this.includeMain) {
      const mainItem = this.readMainItem();
      if (mainItem) items.push(mainItem);
    }

    const selected = this.getSelectedItems();
    selected.forEach(item => {
      items.push({ id: item.id, quantity: 1 });
    });

    if (items.length === 0) return;

    this.setLoading(true);

    MP.Extensions.Cart.add(items)
      .then(() => {
        this.trackAnalytics(selected);
        this.resetSelection();
      })
      .finally(() => this.setLoading(false));
  }

  /**
   * Read the main product line item from the existing product form, preserving
   * the selected variant, quantity, line item properties and selling plan.
   *
   * @returns {Object|false}
   */
  readMainItem() {
    if (!this.mainForm) return false;

    const variantId = this.mainForm.querySelector("[name='id']")?.value;
    if (!variantId) return false;

    const item = {
      id: variantId,
      quantity: parseInt(this.mainForm.querySelector("[name='quantity']")?.value, 10) || 1,
    };

    const sellingPlan = this.mainForm.querySelector("[name='selling_plan']")?.value;
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    this.mainForm.querySelectorAll("[name^='properties']").forEach(input => {
      if (input.type === "checkbox" && !input.checked) return;
      const match = input.name.match(/properties\[(.+)\]/);
      if (match && input.value !== "") properties[match[1]] = input.value;
    });
    if (Object.keys(properties).length > 0) item.properties = properties;

    return item;
  }

  /**
   * Push a GA4 event per complementary product added (Analytics requirement)
   *
   * @param {Array} selected
   */
  trackAnalytics(selected) {
    window.dataLayer = window.dataLayer || [];
    selected.forEach(item => {
      const productId = item.card.dataset.productId;
      window.dataLayer.push({
        event: "complementary_product_added_to_cart",
        parent_product_id: this.parentProductId,
        complementary_product_id: productId,
      });
    });
  }

  /**
   * Clear the current selection after a successful add
   */
  resetSelection() {
    this.cards.forEach(card => {
      const checkbox = card.querySelector("[data-complementary-checkbox]");
      if (checkbox) checkbox.checked = false;
      const select = card.querySelector("[data-complementary-variant]");
      if (select) select.selectedIndex = 0;
    });
    this.recalculate();
  }

  /**
   * Toggle the loading / disabled state of the add button
   *
   * @param {boolean} loading
   */
  setLoading(loading) {
    if (!this.addButton) return;
    if (loading) {
      this.addButton.classList.add("loading");
      this.addButton.setAttribute("aria-disabled", "true");
      this.addButton.querySelector(".spinner-overlay")?.classList.remove("d-none");
    } else {
      this.addButton.classList.remove("loading");
      this.addButton.removeAttribute("aria-disabled");
      this.addButton.querySelector(".spinner-overlay")?.classList.add("d-none");
    }
  }
}

export default ComplementaryProducts;
